import {
  Badge,
  Button,
  Icon,
  SearchInputTypes,
  Shadow,
  Text,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import React from 'react';
import { useRecoilValue } from 'recoil';

import { useConsortiumActions } from '../../../../../../store/consortium/actions';
import { customerConsortiumSectionStatusState } from '../../../../../../store/consortium/atom';

import { IsClosed } from './components/IsClosed';
import { IsNotClosed } from './components/IsNotClosed';

interface SectionProps {
  children: React.ReactNode;
  searchInputProps: SearchInputTypes.Props;
  title: string;
}

export const Section: React.FC<SectionProps> = ({ children, searchInputProps, title }) => {
  const customerSectionStatus = useRecoilValue(customerConsortiumSectionStatusState);
  const isCustomerSectionClosed = customerSectionStatus === 'closed';
  const isCustomerSectionEdit = customerSectionStatus === 'edit';
  const isMobile = useBreakpointValue({ base: true, sm: false });

  const { updateCustomerStatus } = useConsortiumActions();

  if (isMobile) {
    return (
      <VStack space={Spacing['inline.xxxs']}>
        {!isCustomerSectionClosed && (
          <Text
            fontSize={Typography['fontsize.lg']}
            py={Spacing['stack.nano']}
            variant="semibold"
            color="neutral.medium">
            {title}
          </Text>
        )}
        {!isCustomerSectionClosed && (
          <IsNotClosed searchInputProps={searchInputProps} isMobile={isMobile}>
            {children}
          </IsNotClosed>
        )}
        {isCustomerSectionClosed && <IsClosed title={title} />}
      </VStack>
    );
  }

  return (
    <Shadow level={1} borderTopRadius={BoxRadius.sm} borderBottomRadius={BoxRadius.sm}>
      <VStack
        w="full"
        bg="neutral.white"
        space={Spacing['inline.xxs']}
        borderTopRadius={BoxRadius.sm}
        {...(isCustomerSectionClosed && { borderBottomRadius: BoxRadius.sm })}>
        <VStack p={Spacing['stack.xxs']} borderBottomWidth="1px" borderBottomColor="neutral.light">
          <HStack alignItems="center" justifyContent="space-between">
            <Text
              fontSize={Typography['fontsize.lg']}
              variant="semibold"
              color="brand.primary.principal">
              {title}
            </Text>
            {(isCustomerSectionClosed || isCustomerSectionEdit) && (
              <HStack space={Spacing['inline.xxxs']} alignItems="center">
                <Badge
                  testId={`customer-section-${isCustomerSectionClosed ? 'closed' : 'edit'}-badge`}
                  backgroundColor="feedback.success.default"
                  textColor="neutral.white">
                  Concluído
                </Badge>
                <Button
                  variant="textLink"
                  _text={{ fontFamily: Typography['fontfamily.base.semibold'] }}
                  onPress={() => updateCustomerStatus(isCustomerSectionClosed ? 'edit' : 'closed')}
                  rightIcon={
                    <Icon
                      name={isCustomerSectionClosed ? 'Reverse' : 'ChevronRight'}
                      size="xxs"
                      color={Color['brand.tertiary.principal']}
                    />
                  }>
                  {isCustomerSectionClosed ? 'TROCAR CLIENTE' : 'MANTER CLIENTE'}
                </Button>
              </HStack>
            )}
          </HStack>
        </VStack>
        {!isCustomerSectionClosed && (
          <IsNotClosed isMobile={isMobile} searchInputProps={searchInputProps}>
            {children}
          </IsNotClosed>
        )}
        {isCustomerSectionClosed && <IsClosed title={title} />}
      </VStack>
    </Shadow>
  );
};
